import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import _ from 'lodash';

import {Feedback, CardType, LicenseStatus, TrayType, ExplorerContentTypes} from 'constants/dataEnum';

import withIntl from 'lib/withIntl';
import {find} from 'lib/objectHelper';
import {checkContainIllegalChars} from 'lib/stringHelper';

import {showLoading, showSnackbar, hideLoading} from 'stores/actions/appStateAction';
import {showModal, hideModal} from 'stores/actions/modalStateAction';
import {
  saveProtocol,
  renameProtocolFromProtocolEditor,
  newProtocol,
  fetchProtocolsAndFoldersByFolderId,
  formatProtocolsAndFolders,
  updateProtocolState,
} from 'stores/business/protocolManagementBusiness';
import {startRunProtocol} from 'stores/business/runtimeBusiness';
import {isTouchProbeExpanded, safeTouchProbeRetract} from 'stores/business/instrumentBusiness';
import {checkInvalidTask} from 'stores/business/taskListBusinessAction';
import {setAllCardIsSelected} from 'stores/actions/cardStateAction';
import {setAvailableProtocols, setLastOpenedProtocol} from 'stores/actions/protocolAction';
import {useStaticCallback} from 'lib/customHooks';
import {checkIsTouchProbeExtendedWithModal} from 'stores/business/modalStateBusinessAction';
import {showAutosaveConfirmationModal} from 'stores/business/commonProtocolManagementBusiness';
import {showModalConvertToTessaraProtocol} from 'stores/business/commonModalBusiness';
import {saveState} from 'stores/business/stateBusiness';
import {checkLoadCellError} from 'stores/business/loadCellErrorBusiness';
import {getCurrentProtocolFolder} from 'stores/selectors/protocolExplorerSelectors';
import {checkAndHandleIsAllowedToStartProcess} from 'stores/business/instrumentStatusBusiness';
import {InstrumentProcessType} from 'constants/instrumentStatusEnum';
import {checkNestForUntrainedPlates} from 'stores/business/plateRecognitionBusiness';
import {selectIsPlateRecognitionEnabled} from 'stores/selectors/plateRecognitionSelectors';
import ProtocolEditorHeader from './ProtocolEditorHeader';

function mapStateToProps(state) {
  const getUndoRedoState = (prefixKey) => {
    const selectedUndoRedoState = state.undoRedoState[prefixKey];

    return selectedUndoRedoState ?? {future: [], present: {}, past: []};
  };
  return {
    instrumentState: state.instrumentState,
    protocolState: state.protocolState,
    licenseStatus: state.licenseState.status,
    protocolConfig: state.configState.protocolConfig,
    globalUndoRedoState: getUndoRedoState('global'),
    cardStateCards: state.cardState.cards,
    trayType: state.trayCalibrationState.trayType,
    trayId: state.trayCalibrationState.id,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
    appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
    appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
    protocolManagementBusinessRenameProtocolFromProtocolEditor: bindActionCreators(
      renameProtocolFromProtocolEditor,
      dispatch,
    ),
    protocolManagementBusinessNewProtocol: bindActionCreators(newProtocol, dispatch),
    runtimeBusinessStartRunProtocol: bindActionCreators(startRunProtocol, dispatch),
    taskListBusinessActionCheckInvalidTask: bindActionCreators(checkInvalidTask, dispatch),
    modalStateActionShowModal: bindActionCreators(showModal, dispatch),
    modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
    cardStateActionSetAllCardIsSelected: bindActionCreators(setAllCardIsSelected, dispatch),
    protocolManagementBusinessFetchProtocolsAndFoldersByFolderId: bindActionCreators(
      fetchProtocolsAndFoldersByFolderId,
      dispatch,
    ),
    protocolManagementBusinessFormatProtocolsAndFolders: bindActionCreators(formatProtocolsAndFolders, dispatch),
    protocolManagementBusinessUpdateProtocolState: bindActionCreators(updateProtocolState, dispatch),
    protocolActionSetAvailableProtocols: bindActionCreators(setAvailableProtocols, dispatch),
    protocolActionSetLastOpenedProtocol: bindActionCreators(setLastOpenedProtocol, dispatch),
    instrumentBusinessIsTouchProbeExpanded: bindActionCreators(isTouchProbeExpanded, dispatch),
    instrumentBusinessSafeTouchProbeRetract: bindActionCreators(safeTouchProbeRetract, dispatch),
    modalStateBusinessActionShowTouchProbeExtendedModal: bindActionCreators(
      checkIsTouchProbeExtendedWithModal,
      dispatch,
    ),
    protocolEditorBusinessShowAutosaveConfirmationModal: bindActionCreators(showAutosaveConfirmationModal, dispatch),
    commonModalBusinessShowModalConvertToTessaraProtocol: bindActionCreators(
      showModalConvertToTessaraProtocol,
      dispatch,
    ),
    stateBusinessSaveState: bindActionCreators(saveState, dispatch),
  };
}

function ProtocolEditorHeaderContainer({
  instrumentState,
  cardStateCards,
  protocolState,
  licenseStatus,
  trayType,
  trayId,
  globalUndoRedoState,
  appStateActionShowLoading,
  appStateActionShowSnackbar,
  isTessaraSuperUser,
  isTessaraProtocol,
  protocolManagementBusinessRenameProtocolFromProtocolEditor,
  protocolManagementBusinessNewProtocol,
  protocolManagementBusinessUpdateProtocolState,
  runtimeBusinessStartRunProtocol,
  taskListBusinessActionCheckInvalidTask,
  modalStateActionShowModal,
  modalStateActionHideModal,
  onUndo,
  onRedo,
  headerRef,
  intl,
  protocolConfig,
  protocolManagementBusinessFetchProtocolsAndFoldersByFolderId,
  protocolManagementBusinessFormatProtocolsAndFolders,
  protocolActionSetAvailableProtocols,
  protocolActionSetLastOpenedProtocol,
  modalStateBusinessActionShowTouchProbeExtendedModal,
  protocolEditorBusinessShowAutosaveConfirmationModal,
  commonModalBusinessShowModalConvertToTessaraProtocol,
  stateBusinessSaveState,
  runOnly,
}) {
  const hasNoIgnoredTask = !!find(
    cardStateCards,
    ({isIgnored, type, invalidTypes}) =>
      // eslint-disable-next-line implicit-arrow-linebreak
      !isIgnored && !invalidTypes?.length && type !== CardType.CONFIRMATION && type !== CardType.DELAY,
  );
  const dispatch = useDispatch();

  const currentProtocolFolder = useSelector(getCurrentProtocolFolder);
  const isPlateRecognitionEnabled = useSelector(selectIsPlateRecognitionEnabled);

  const [headerKey, setHeaderKey] = useState(1);
  const [protocolName, setProtocolName] = useState('New Protocol');
  const [protocolList, setProtocolList] = useState([]);
  const [protocolId, setProtocolId] = useState('');

  useEffect(() => {
    const init = async () => {
      const {availableProtocols, lastOpenedProtocol} = protocolState;

      setProtocolList(availableProtocols);

      let currentProtocolName = lastOpenedProtocol?.name;

      if (!currentProtocolName) {
        currentProtocolName = await protocolManagementBusinessNewProtocol();
      }

      setProtocolName(currentProtocolName);
      setProtocolId(lastOpenedProtocol?.id);
    };

    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {lastOpenedProtocol} = protocolState;
    const {id, name} = lastOpenedProtocol || {};

    if (name && id && (id !== protocolId || name !== protocolName)) {
      setProtocolId(id);
      setProtocolName(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolState]);

  const checkMaxCharacterExceeded = (name) => name.length > protocolConfig.nameMaxLength;

  const checkProtocolNameExist = (name) => {
    if (!protocolId) {
      return protocolList.find(
        (p) => p.name.toLowerCase() === name.trim().toLowerCase() && p.type === ExplorerContentTypes.PROTOCOL,
      );
    }

    return protocolList.find(
      (p) =>
        p.ID !== protocolId &&
        p.name.toLowerCase() === name.trim().toLowerCase() &&
        p.type === ExplorerContentTypes.PROTOCOL,
    );
  };

  const onProtocolNameChange = (value) => {
    const newValue = value.trim();
    let errorMessage = null;

    const newHeaderKey = headerKey + 1;

    if (newValue === protocolName) {
      return;
    }

    if (!newValue) {
      errorMessage = intl.formatMessage({id: 'explorer.modal.rename.warning.required'});
    }

    if (checkContainIllegalChars(newValue)) {
      errorMessage = intl.formatMessage({id: 'explorer.modal.rename.warning.illegalCharacters'});
    }

    if (checkMaxCharacterExceeded(newValue)) {
      errorMessage = intl.formatMessage({id: 'explorer.modal.rename.warning.modalMaxCharacters'});
    }

    if (checkProtocolNameExist(newValue)) {
      errorMessage = intl.formatMessage(
        {id: 'explorer.modal.rename.warning.nameAlreadyExists'},
        {
          type: 'Protocol',
        },
      );
    }

    if (errorMessage) {
      setProtocolName(protocolName);
      setHeaderKey(newHeaderKey);
      appStateActionShowSnackbar(Feedback.ERROR, errorMessage);

      return;
    }

    setProtocolName(newValue);
    setHeaderKey(newHeaderKey);
    protocolManagementBusinessRenameProtocolFromProtocolEditor(newValue);
  };

  const onStartClick = useStaticCallback(async () => {
    dispatch(showLoading());
    const isLoadCellError = await dispatch(checkLoadCellError(null, onStartClick));
    const isAllowed = await dispatch(checkAndHandleIsAllowedToStartProcess(InstrumentProcessType.PROTOCOL_EXECUTION));
    dispatch(hideLoading());

    if (isLoadCellError || !isAllowed) return;

    modalStateBusinessActionShowTouchProbeExtendedModal(
      () => {
        const startProtocol = async () => {
          appStateActionShowLoading(intl.formatMessage({id: 'protocolEditor.StartButton.loading'}));
          await runtimeBusinessStartRunProtocol();
        };
        const updateAvailableProtocolList = async () => {
          const allProtocolsResponse = await protocolManagementBusinessFetchProtocolsAndFoldersByFolderId(
            currentProtocolFolder?.id || null,
          );
          const [allProtocolList] = protocolManagementBusinessFormatProtocolsAndFolders(allProtocolsResponse);
          protocolActionSetAvailableProtocols(allProtocolList || []);
        };
        let isInvalid = false;
        let validCardCount = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const cardId of Object.keys(cardStateCards)) {
          const card = cardStateCards[cardId];
          const {isIgnored, invalidTypes, type} = card;
          if (
            type === CardType.SERIAL_DILUTION ||
            type === CardType.DELAY ||
            type === CardType.CONFIRMATION ||
            type === CardType.NORMALIZATION
          ) {
            if (type === CardType.SERIAL_DILUTION || type === CardType.NORMALIZATION) {
              validCardCount += 1;
            }
            continue;
          }
          if (!isIgnored) {
            const isHighVolume = protocolState.lastOpenedProtocol?.trayType === TrayType.HIGH_VOLUME_CADDY;
            const checkIsInvalid = taskListBusinessActionCheckInvalidTask(
              cardId,
              true,
              false,
              false,
              false,
              isHighVolume,
            );
            if (invalidTypes?.length || checkIsInvalid) {
              isInvalid = true;
            } else {
              validCardCount += 1;
            }
          }
        }
        if (isInvalid && validCardCount) {
          modalStateActionShowModal(
            intl.formatMessage({id: 'protocolEditor.startRunProtocol.invalidCardTriggered.title'}),
            intl.formatMessage({id: 'protocolEditor.startRunProtocol.invalidCardTriggered.content'}),
            [
              {
                className: 'btn-lg',
                style: 'primary',
                handlerClick: () => {
                  modalStateActionHideModal();
                  startProtocol();
                  updateAvailableProtocolList();
                },
                text: intl.formatMessage({id: 'protocolEditor.startRunProtocol.invalidCardTriggered.confirmButton'}),
              },
              {
                className: 'btn-lg',
                style: 'secondary',
                handlerClick: () => modalStateActionHideModal(),
                text: intl.formatMessage({id: 'protocolEditor.startRunProtocol.invalidCardTriggered.cancelButton'}),
              },
            ],
            () => modalStateActionHideModal(),
            '',
            true,
          );
        } else if (validCardCount) {
          startProtocol();
          updateAvailableProtocolList();
        }
      },
      false,
      true,
    );
  });

  const handleOnStartClick = useStaticCallback(async () => {
    if (isPlateRecognitionEnabled) {
      await dispatch(checkNestForUntrainedPlates(onStartClick));
    } else {
      onStartClick();
    }
  }, [dispatch, onStartClick]);

  const isUndoEnabled = () => globalUndoRedoState?.past?.length > 0;

  const isRedoEnabled = () => globalUndoRedoState?.future?.length > 0;

  const isTrayCompatible = () => {
    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType || '';

    return trayId < 0 || protocolTrayType === trayType || (!protocolTrayType && trayType === TrayType.NORMAL_CADDY);
  };

  const hasCards = () => {
    const cardEntries = Object.values(cardStateCards);

    if (cardEntries.length === 0) {
      return false;
    }

    // Only one card and it's EnableHumidifier
    if (cardEntries.length === 1 && cardEntries[0].type === CardType.ENABLE_HUMIDIFIER) {
      return false;
    }

    return true;
  };

  const isStartEnabled = () => {
    const {isSimulationMode, isAutomationMode} = instrumentState;
    const hasLicense =
      licenseStatus === LicenseStatus.TRIAL ||
      licenseStatus === LicenseStatus.TEMPORARY ||
      licenseStatus === LicenseStatus.PERMANENT ||
      licenseStatus === LicenseStatus.PERMANENT_EXPIRED;

    return (
      hasCards() && ((hasNoIgnoredTask && hasLicense && isTrayCompatible()) || (isSimulationMode && !isAutomationMode))
    );
  };

  const onTessaraSuperUserProtocolConvert = (convertToTessara = true) => {
    commonModalBusinessShowModalConvertToTessaraProtocol(async () => {
      if (protocolId !== '-1')
        await protocolManagementBusinessUpdateProtocolState(protocolId, {IsTessaraProtocol: convertToTessara});

      const {lastOpenedProtocol} = protocolState;
      protocolActionSetLastOpenedProtocol({...lastOpenedProtocol, isTessaraProtocol: convertToTessara});
      await stateBusinessSaveState();
    }, convertToTessara);
  };

  return (
    <ProtocolEditorHeader
      key={headerKey}
      title={protocolName}
      onStartClick={handleOnStartClick}
      onProtocolNameChange={onProtocolNameChange}
      onTessaraSuperUserProtocolConvert={isTessaraSuperUser ? onTessaraSuperUserProtocolConvert : null}
      isTessaraProtocol={isTessaraProtocol}
      disableStart={!isStartEnabled()}
      disableUndo={!isUndoEnabled()}
      disableRedo={!isRedoEnabled()}
      isTrayCompatible={isTrayCompatible()}
      onUndo={onUndo}
      onRedo={onRedo}
      headerRef={headerRef}
      runOnly={runOnly}
      onSaveChanges={() => protocolEditorBusinessShowAutosaveConfirmationModal(null, false, saveProtocol)}
    />
  );
}

ProtocolEditorHeaderContainer.propTypes = {
  instrumentState: PropTypes.object.isRequired,
  cardStateCards: PropTypes.object,
  protocolState: PropTypes.object.isRequired,
  licenseStatus: PropTypes.string.isRequired,
  trayType: PropTypes.string.isRequired,
  trayId: PropTypes.number.isRequired,
  globalUndoRedoState: PropTypes.object.isRequired,
  runOnly: PropTypes.bool,
  isTessaraSuperUser: PropTypes.bool.isRequired,
  isTessaraProtocol: PropTypes.bool.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  protocolManagementBusinessRenameProtocolFromProtocolEditor: PropTypes.func.isRequired,
  protocolManagementBusinessNewProtocol: PropTypes.func.isRequired,
  protocolManagementBusinessUpdateProtocolState: PropTypes.func.isRequired,
  runtimeBusinessStartRunProtocol: PropTypes.func.isRequired,
  taskListBusinessActionCheckInvalidTask: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  commonModalBusinessShowModalConvertToTessaraProtocol: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  headerRef: PropTypes.object,
  intl: PropTypes.any.isRequired,
  protocolConfig: PropTypes.object.isRequired,
  protocolManagementBusinessFetchProtocolsAndFoldersByFolderId: PropTypes.func.isRequired,
  protocolManagementBusinessFormatProtocolsAndFolders: PropTypes.func.isRequired,
  protocolActionSetAvailableProtocols: PropTypes.func.isRequired,
  protocolActionSetLastOpenedProtocol: PropTypes.func.isRequired,
  modalStateBusinessActionShowTouchProbeExtendedModal: PropTypes.func.isRequired,
  protocolEditorBusinessShowAutosaveConfirmationModal: PropTypes.func.isRequired,
  stateBusinessSaveState: PropTypes.func.isRequired,
};

ProtocolEditorHeaderContainer.defaultProps = {
  headerRef: null,
  cardStateCards: {},
  runOnly: false,
};

function areEqual(prevProps, nextProps) {
  return (
    _.isEqual(prevProps.headerRef, nextProps.headerRef) &&
    _.isEqual(prevProps.isTessaraSuperUser, nextProps.isTessaraSuperUser) &&
    _.isEqual(prevProps.isTessaraProtocol, nextProps.isTessaraProtocol)
  );
}

export default React.memo(
  connect(mapStateToProps, mapDispatchToProps)(withIntl(ProtocolEditorHeaderContainer)),
  areEqual,
);
